/**
 * CONTACT-MGMT (CM-6) — contact detail (`/admin/contacts/:contactId`).
 *
 * Read-only summary of all fields + a where-used panel (CM-D7) with three
 * sections (Projects · Contracts · Invoices), each row showing code/name +
 * the party slot (customer/contractor/billing). Edit (dialog) + Delete (soft,
 * confirmed) are gated to the contact write roles (CM-D1). Project refs link
 * to the project detail page; contract/invoice refs render their code/name
 * (no standalone detail route exists yet).
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { PageHeading } from "@aeriosiq/ui";
import { countryByCode } from "../../sites/shared/iso3166.js";
import { ToastProvider, useToast } from "../../account/ToastContext.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import { ContactForm } from "./ContactForm.js";
import {
  useContactQuery,
  useContactUsageQuery,
  useSoftDeleteContactMutation,
} from "./useContacts.js";
import { useContactWriteAccess } from "./useContactPermissions.js";
import type { ContactUsageRef, ContactUsageSlot } from "./types.js";

const SLOT_LABEL: Record<ContactUsageSlot, string> = {
  customer: "Customer",
  contractor: "Contractor",
  billing: "Billing",
};

export function ContactDetailPage(): ReactElement {
  return (
    <ToastProvider>
      <ContactDetailPageInner />
    </ToastProvider>
  );
}

function ContactDetailPageInner(): ReactElement {
  const { contactId } = useParams<{ contactId: string }>();
  const id = contactId ?? null;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canUpdate, canDelete } = useContactWriteAccess();

  const { data: contact, isPending, isError } = useContactQuery(id);
  const usage = useContactUsageQuery(id);
  const softDelete = useSoftDeleteContactMutation();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isPending) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError) {
    return (
      <Box sx={{ maxWidth: 960, mx: "auto", py: 4 }}>
        <Typography color="error">Couldn&apos;t load this contact.</Typography>
        <Button
          sx={{ mt: 2 }}
          onClick={() => {
            void navigate("/admin/contacts");
          }}
        >
          Back to contacts
        </Button>
      </Box>
    );
  }

  async function handleDelete(): Promise<void> {
    if (!id) return;
    try {
      await softDelete.mutateAsync(id);
      toast("Contact deleted.");
      void navigate("/admin/contacts");
    } catch {
      toast("Couldn't delete the contact.", "error");
    } finally {
      setConfirmDelete(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", pb: 6 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <PageHeading>{contact.companyName ?? "Contact"}</PageHeading>
          {contact.isDeleted ? (
            <Chip label="Deleted" size="small" color="default" />
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1}>
          {canUpdate ? (
            <Button
              variant="outlined"
              onClick={() => {
                setEditOpen(true);
              }}
            >
              Edit
            </Button>
          ) : null}
          {canDelete && !contact.isDeleted ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              Delete
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Postal address
        </Typography>
        <FieldRow label="Street" value={contact.street} />
        <FieldRow label="Post code" value={contact.postCode} />
        <FieldRow label="City" value={contact.city} />
        <FieldRow
          label="Country"
          value={
            contact.countryCode
              ? `${contact.countryCode}${
                  countryByCode(contact.countryCode)
                    ? ` — ${countryByCode(contact.countryCode)?.name ?? ""}`
                    : ""
                }`
              : null
          }
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant="overline" color="text.secondary">
          Contact
        </Typography>
        <FieldRow label="Contact person" value={contact.contactPersonName} />
        <FieldRow label="Email" value={contact.contactEmail} />
        <FieldRow label="Phone" value={contact.contactPhone} />
        <FieldRow label="Website" value={contact.website} />
        <Divider sx={{ my: 2 }} />
        <Typography variant="overline" color="text.secondary">
          Tax
        </Typography>
        <FieldRow label="VAT ID" value={contact.vatId} />
        <FieldRow label="Tax number" value={contact.taxNumber} />
        <FieldRow label="Notes" value={contact.notes} />
      </Paper>

      <WhereUsedPanel
        loading={usage.isPending}
        error={usage.isError}
        projects={usage.data?.projects ?? []}
        contracts={usage.data?.contracts ?? []}
        invoices={usage.data?.invoices ?? []}
      />

      <Dialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit contact</DialogTitle>
        <DialogContent>
          <ContactForm
            mode="edit"
            initial={contact}
            onCancel={() => {
              setEditOpen(false);
            }}
            onSaved={() => {
              setEditOpen(false);
              toast("Contact updated.");
            }}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete contact?"
        description={`Soft-delete “${
          contact.companyName ?? "this contact"
        }”? It will disappear from the active list but can be restored, and existing project/contract references keep resolving its name.`}
        confirmLabel="Delete contact"
        danger
        pending={softDelete.isPending}
        onConfirm={() => {
          void handleDelete();
        }}
        onCancel={() => {
          setConfirmDelete(false);
        }}
      />
    </Box>
  );
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: string | null;
}): ReactElement {
  return (
    <Stack direction="row" spacing={2} sx={{ py: 0.75 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ width: 160, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">
        {value && value.length > 0 ? value : "—"}
      </Typography>
    </Stack>
  );
}

interface WhereUsedPanelProps {
  loading: boolean;
  error: boolean;
  projects: ContactUsageRef[];
  contracts: ContactUsageRef[];
  invoices: ContactUsageRef[];
}

function WhereUsedPanel({
  loading,
  error,
  projects,
  contracts,
  invoices,
}: WhereUsedPanelProps): ReactElement {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Where used
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Alert severity="error">Couldn&apos;t load where this is used.</Alert>
      ) : (
        <Stack spacing={3}>
          <UsageSection title="Projects" refs={projects} kind="project" />
          <UsageSection title="Contracts" refs={contracts} kind="contract" />
          <UsageSection title="Invoices" refs={invoices} kind="invoice" />
        </Stack>
      )}
    </Paper>
  );
}

function UsageSection({
  title,
  refs,
  kind,
}: {
  title: string;
  refs: ContactUsageRef[];
  kind: "project" | "contract" | "invoice";
}): ReactElement {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title} ({refs.length})
      </Typography>
      {refs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Not used in any {title.toLowerCase()}.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {refs.map((r) => (
            <Stack
              key={`${kind}-${r.id}-${r.slot}`}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              {kind === "project" ? (
                <MuiLink
                  component={RouterLink}
                  to={`/projects/${r.id}`}
                  variant="body2"
                >
                  {refLabel(r)}
                </MuiLink>
              ) : (
                <Typography variant="body2">{refLabel(r)}</Typography>
              )}
              <Chip label={SLOT_LABEL[r.slot]} size="small" />
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function refLabel(r: ContactUsageRef): string {
  const code = r.code ? `#${r.code}` : null;
  if (r.name && code) return `${code} — ${r.name}`;
  if (r.name) return r.name;
  if (code) return code;
  return r.id;
}
